/**
 * Feature Request Board — public roadmap. Members submit ideas + upvote.
 * Admins can change status inline.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowUp, MessageSquare, Plus, X, Loader2, ChevronDown, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  roadmapList, roadmapCreate, roadmapUpvote, roadmapMyUpvotes,
  roadmapAdminUpdate, roadmapAdminDelete, formatApiError,
} from "@/lib/api";
import Footer from "@/components/Footer";

const STATUS_COLORS = {
  open: { bg: "rgba(255,255,255,0.06)", fg: "#B0B0B0", label: "Open" },
  planned: { bg: "rgba(0,240,255,0.15)", fg: "#00F0FF", label: "Planned" },
  building: { bg: "rgba(255,227,0,0.15)", fg: "#FFE300", label: "Building" },
  shipped: { bg: "rgba(57,255,20,0.15)", fg: "#39FF14", label: "Shipped" },
  closed: { bg: "rgba(255,0,60,0.15)", fg: "#FF003C", label: "Closed" },
};

const SORTS = [
  { id: "top", label: "Top" },
  { id: "new", label: "Newest" },
];

export default function Roadmap() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState("top");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [myUpvotes, setMyUpvotes] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "Feature" });
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    roadmapList(sort, statusFilter, categoryFilter).then((d) => {
      setItems(d.items || []);
      setStatuses(d.statuses || []);
      setCategories(d.categories || []);
    });

  useEffect(() => { load(); }, [sort, statusFilter, categoryFilter]); // eslint-disable-line
  useEffect(() => {
    if (user) roadmapMyUpvotes().then((r) => setMyUpvotes(new Set(r.ids || []))).catch(() => {});
    else setMyUpvotes(new Set());
  }, [user?.id]); // eslint-disable-line

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { toast.error("Log in to submit an idea"); return; }
    setSubmitting(true);
    try {
      await roadmapCreate(form);
      toast.success("Idea posted!");
      setForm({ title: "", body: "", category: "Feature" });
      setShowForm(false);
      setMyUpvotes((s) => new Set(s));  // will be re-seeded below
      load();
      roadmapMyUpvotes().then((r) => setMyUpvotes(new Set(r.ids || [])));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUpvote = async (item) => {
    if (!user) { toast.error("Log in to upvote"); return; }
    try {
      const res = await roadmapUpvote(item.id);
      setItems((list) => list.map((x) => x.id === item.id ? { ...x, upvotes: res.upvotes } : x));
      setMyUpvotes((s) => {
        const n = new Set(s);
        if (res.upvoted) n.add(item.id); else n.delete(item.id);
        return n;
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changeStatus = async (item, status) => {
    try {
      const res = await roadmapAdminUpdate(item.id, { status });
      setItems((list) => list.map((x) => x.id === item.id ? res.item : x));
      toast.success(`Status → ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const adminDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await roadmapAdminDelete(item.id);
      setItems((list) => list.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="roadmap-page">
      <div className="max-w-4xl mx-auto px-6 pt-10">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-3">// community roadmap</div>
            <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter">Feature Requests</h1>
            <div className="mono text-xs text-white/50 mt-2 max-w-lg">
              What should Jixxeo build next? Post an idea. Upvote what excites you. Watch it ship.
            </div>
          </div>
          <button
            data-testid="roadmap-new-btn"
            onClick={() => setShowForm((s) => !s)}
            className="bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-5 py-3 rounded-full inline-flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Close" : "Post an idea"}
          </button>
        </div>

        {/* New request form */}
        {showForm && (
          <form onSubmit={submit} data-testid="roadmap-form"
            className="mt-6 glass rounded-2xl p-5 border border-white/10 space-y-3">
            <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#39FF14]">
              <Lightbulb className="w-4 h-4" /> // your idea
            </div>
            <input
              data-testid="roadmap-title"
              value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={120} required minLength={4}
              placeholder="One-line pitch (4-120 chars)"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#39FF14]/60 focus:outline-none" />
            <textarea
              data-testid="roadmap-body"
              value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              maxLength={2000} rows={4}
              placeholder="Details, use cases, why this matters (optional)"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#39FF14]/60 focus:outline-none" />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <select
                data-testid="roadmap-category"
                value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 mono text-[10px] uppercase tracking-widest">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                type="submit" disabled={submitting}
                data-testid="roadmap-submit"
                className="bg-[#39FF14] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-40">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {submitting ? "Posting…" : "Post idea"}
              </button>
            </div>
          </form>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <div className="flex gap-1">
            {SORTS.map((s) => (
              <button key={s.id}
                data-testid={`roadmap-sort-${s.id}`}
                onClick={() => setSort(s.id)}
                className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                  sort === s.id ? "bg-white text-black font-black" : "border border-white/10 text-white/60 hover:text-white"
                }`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="h-4 w-px bg-white/10" />
          <FilterChip label="All statuses" active={!statusFilter} onClick={() => setStatusFilter("")} />
          {statuses.map((s) => (
            <FilterChip key={s} testId={`roadmap-filter-${s}`}
              label={STATUS_COLORS[s]?.label || s}
              color={STATUS_COLORS[s]?.fg}
              active={statusFilter === s}
              onClick={() => setStatusFilter(statusFilter === s ? "" : s)} />
          ))}
        </div>

        {/* List */}
        <div className="mt-6 space-y-2" data-testid="roadmap-list">
          {items.length === 0 && (
            <div className="rounded-2xl p-10 border border-dashed border-white/10 text-center text-white/50 mono text-[10px] uppercase tracking-widest">
              // no ideas yet. be the first!
            </div>
          )}
          {items.map((it) => {
            const s = STATUS_COLORS[it.status] || STATUS_COLORS.open;
            const isUpvoted = myUpvotes.has(it.id);
            return (
              <div key={it.id} data-testid={`roadmap-item-${it.id}`}
                className="glass rounded-2xl p-4 border border-white/10 hover:border-white/20 flex items-start gap-4 transition-colors">
                {/* Upvote button */}
                <button
                  data-testid={`roadmap-upvote-${it.id}`}
                  onClick={() => toggleUpvote(it)}
                  className={`shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center transition-colors ${
                    isUpvoted ? "bg-[#39FF14] text-black" : "bg-white/5 hover:bg-white/10 text-white/80"
                  }`}>
                  <ArrowUp className="w-4 h-4" />
                  <span className="font-heading font-black text-sm">{it.upvotes ?? 0}</span>
                </button>
                {/* Body */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mono text-[9px] uppercase tracking-widest">
                    <span style={{ color: s.fg, background: s.bg }} className="px-2 py-0.5 rounded-full font-bold">
                      {s.label}
                    </span>
                    <span className="text-white/40">· {it.category}</span>
                    <span className="text-white/40 truncate">· by {it.author_name}</span>
                  </div>
                  <div className="font-heading font-black text-lg text-white mt-1 leading-tight">
                    {it.title}
                  </div>
                  {it.body && (
                    <div className="text-sm text-white/70 mt-1 whitespace-pre-line">{it.body}</div>
                  )}
                  {it.admin_note && (
                    <div className="mt-2 rounded-lg p-2 border border-[#FFE300]/30 bg-[#FFE300]/5 text-[#FFE300] mono text-[10px] uppercase tracking-widest">
                      // admin: {it.admin_note}
                    </div>
                  )}
                  {isAdmin && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="mono text-[9px] uppercase tracking-widest text-white/40">admin:</span>
                      {statuses.map((st) => (
                        <button key={st} data-testid={`roadmap-admin-status-${it.id}-${st}`}
                          onClick={() => changeStatus(it, st)}
                          className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full transition-colors ${
                            it.status === st
                              ? "text-black font-bold"
                              : "text-white/50 border border-white/10 hover:text-white"
                          }`}
                          style={{
                            background: it.status === st ? STATUS_COLORS[st]?.fg : "transparent",
                          }}>
                          {STATUS_COLORS[st]?.label || st}
                        </button>
                      ))}
                      <button
                        data-testid={`roadmap-admin-delete-${it.id}`}
                        onClick={() => adminDelete(it)}
                        className="mono text-[9px] uppercase tracking-widest text-[#FF003C]/70 hover:text-[#FF003C] px-2">
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function FilterChip({ label, color, active, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
        active ? "text-black font-black" : "border border-white/10 text-white/60 hover:text-white"
      }`}
      style={{
        background: active ? (color || "#FFE300") : "transparent",
      }}>
      {label}
    </button>
  );
}
